/**
 * A credit note's line items, edited in place.
 *
 * ## ⚠️ IT IS MODELLED ON `@/components/invoices/InvoiceLineItemsTable` AND IS
 * DELIBERATELY SMALLER
 *
 * That grid is ~9,900 lines and this one is a fraction of it. The difference is
 * **not** unfinished work: five of the things it does have no counterpart here,
 * and each absence below is a decision with a reason. Read them before "filling
 * a gap" — every one of the five would be a real defect if added.
 *
 * What IS borrowed, unchanged, because it is this repo's house convention:
 * `table-fixed` plus a `<colgroup>` as the single source of column widths, a
 * content `min-w-` on every cell, the two pinned trailing columns, the `compact`
 * flag that strips labels and hints inside a grid cell, the stacked card below
 * `md`, and the row-at-a-time save that reports partial success.
 *
 * ## The five ways a credit-note line differs — every one an owner decision
 *
 * ### 1. `description` is REQUIRED and can never be cleared
 *
 * On the quote, invoice and deal grids `description: null` means **DERIVE the
 * wording at push time** from the product, project and cities, and it keeps
 * following them. Here it does not: a credit note must print **what the customer
 * was actually billed**, not what that product's description says today, so the
 * wording is **FROZEN** — resolved once, at copy time, by the same function the
 * push uses, and stored.
 *
 * ⛔ **So there is NO "Reset to derived" control and there must not be one**, and
 * `null` is a **422** rather than a clear. The nullability is where that decision
 * is made structural: {@link CreditNoteLine.description} is `string`, not
 * `string | null`, so no code path can reintroduce derivation and no renderer
 * needs a fallback. A `?? ''` or a `?? composeLineDescription(...)` in this file
 * would be dead code that reads as a live rule. {@link DescriptionControl} says
 * the same thing at the cell, because a reader arriving from the invoice grid
 * will look for the control that is not there.
 *
 * ### 2. There are NO project, regions or inclusions controls
 *
 * ⚠️ SUPERSEDED FOR CITIES (owner 2026-10-27, migration 331): a line copied from
 * an invoice line records WHICH of that line's cities it credits
 * (`cities` / `cancelsWholeLine`, written as `cityIds`, a whole set). They are
 * CANCELLED downstream once the note is approved. The picker offers only the
 * invoice line's cities not on another non-void credit note (`creditedCityIds`,
 * this line's own cities counting as available); a line typed by hand has no
 * invoice line and so no cities. Changing cities re-prices the line with
 * `proRataCreditLine` unless its price or discount was typed since load. Xero
 * prints ` — Cancelled cities: …` after the stored description on a partial
 * credit; a whole-line credit prints nothing extra. The paragraphs below stand
 * for projects, regions and inclusions.
 *
 * Migration 198 creates **no** city, region or inclusion child tables for
 * credit-note lines, and that is coherent rather than missing. On the other three
 * grids that geography largely exists to COMPOSE wording at push time; this table
 * **stores the wording instead**, so the inputs to a derivation nobody performs
 * have nothing to do.
 *
 * ⛔ **Do not add them.** There is no wire key to carry cities, regions or
 * inclusions, and a control whose value cannot be sent is the defect this repo
 * catalogues as *a value nothing renders*, arriving from the other direction.
 *
 * ⚠️ **`projectId` IS a key on {@link CreditNoteLineCreateInput} and is
 * deliberately NOT in {@link Draft}.** A copied line carries its source line's
 * project, set server-side; nothing on this screen may change it. Leaving the key
 * reachable in a payload builder is how it eventually goes out as `null` on an
 * unrelated edit — **an explicit `null` CLEARS** — so the guard is structural
 * rather than remembered: `projectId` is not a `Draft` key at all, so no payload
 * here can express one and `tsc` refuses a call site that tries. The same shape
 * as `projectId` on the document forms and `ownerId` on quotes.
 *
 * ### 3. `unitPrice` MAY BE NEGATIVE
 *
 * A DEDUCTION LINE is ordinary business: INV-0886 carries one reading *"Deduction
 * for Cape Town Participation Fee & Travel Package"* at **`-4150.00`**, and
 * migration 171 dropped `ROUND(GREATEST(…, 0), 2)` from the generated money
 * columns so the total is genuinely negative rather than silently rewritten to
 * zero.
 *
 * ⛔ **Never `Math.max(0, …)`, never `Math.abs(…)`, and never render a negative as
 * zero.** The database used to do exactly that and the migration exists to stop
 * it. ⚠️ **And never assume a credit note's lines are the negative of an
 * invoice's** — a credit note's sign convention is whatever its lines carry.
 *
 * ### 4. `discountAmount` STAYS NON-NEGATIVE
 *
 * **A negative price is a credit; a negative discount is a SURCHARGE.** See
 * {@link PRICE_PATTERN} and {@link DISCOUNT_AMOUNT_PATTERN} — two named
 * constants, differing by one character, which **must never converge**. A single
 * pattern served both fields in four files in this repo and was split for exactly
 * this reason.
 *
 * ### 5. The money columns are GENERATED
 *
 * `lineSubtotal`, `lineTax` and `lineTotal` are generated columns and **sending
 * one is a 422**. Their absence from {@link CreditNoteLineCreateInput} is what
 * stops a form built from the read shape round-tripping them back, and nothing
 * here ever puts one in a payload.
 *
 * Tax is charged on the **already-rounded** subtotal and the total is the sum of
 * two rounded parts, so a client-side preview must repeat those steps **in that
 * order** or it disagrees by a cent with the figure the customer sees. That is
 * {@link computeGeneratedLinePreview}'s whole job — **never hand-roll a total.**
 *
 * ## The copy-from-invoice control — the path a credit note is normally raised down
 *
 * Retyping an invoice's lines to credit them is the error-prone half of this
 * screen, so {@link CopyFromInvoiceDialog} does it in ONE request.
 *
 * ⛔ **An absent `invoiceProductIds` means EVERY line on that invoice; `[]` is a
 * 422, NOT "none".** Those are one keystroke apart in any form that builds an
 * array from a selection and they mean opposite things, so an empty selection is
 * **refused here with a sentence** and never sent.
 *
 * ⚠️ **The server refuses a mismatched company or currency with a 422 naming
 * `invoiceId`** — surfaced verbatim. The picker is scoped to this note's company
 * and currency where it can be (it reads the note once, on open, for the company
 * id), so the refusal is a backstop rather than the ordinary outcome; where that
 * read fails the list is unscoped and the server's sentence is the whole answer.
 *
 * ⚠️ **The copy is a SNAPSHOT, taken now**, and every label says so. Editing the
 * source invoice afterwards changes nothing here — do not word any control for it
 * as "link to" or "keep in step with" an invoice.
 *
 * ## ⚠️ Every line write REWRITES the note's four header totals, server-side
 *
 * The create, the copy, the update and the delete each re-derive `subTotal`,
 * `discountTotal`, `taxTotal` and `grandTotal` in their own transaction, and none
 * of them returns the new header. So {@link CreditNoteLineItemsTableProps.onChanged}
 * fires after any successful write and **the parent MUST re-read the credit
 * note**. Nothing here ever recomputes a header total: the footer is
 * `sumDecimalStrings` over the server's own `lineTotal` values, labelled as what
 * it is.
 */

import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import {
  computeGeneratedLinePreview,
  sumDecimalStrings,
  type GeneratedLinePreview,
} from '@/lib/decimal';
import {
  DISCOUNT_MODE_GROUP_LABEL,
  DISCOUNT_MODE_OPTIONS,
  discountModeOf,
} from '@/lib/discount-mode';
import { formatDecimalString } from '@/lib/format';
import { proRataCreditLine } from '@/lib/credit-city-pro-rata';
import { creditCityRefusalMessage } from './credit-copy-errors';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { useXero } from '@/lib/xero-context';
import {
  hasEffectiveRate,
  isSalesTaxRate,
  lineTaxRates,
  sameTaxCode,
  taxRateHint,
  useDocumentTaxCodes,
  useXeroTaxRates,
} from '@/lib/xero-tax-rates';
/*
 * ⚠️ `XeroTaxRate` COMES FROM `@/lib/types`, NOT FROM `@/lib/xero-tax-rates`.
 * That module IMPORTS the type to build its hooks and helpers on and does not
 * re-export it, so reaching for it there is a compile error rather than a
 * narrowing — which is how `tsc` caught this. `InvoiceLineItemsTable` takes it
 * from `types` for the same reason; the wire shape lives with the other wire
 * shapes, and the helper module owns the behaviour rather than the contract.
 */
import type {
  XeroTaxRate,
  CopyInvoiceLinesInput,
  CreditNoteLine,
  CreditNoteLineCreateInput,
  CreditNoteLineUpdateInput,
  Invoice,
  InvoiceProduct,
} from '@/lib/types';
import {
  Badge,
  Button,
  Checkbox,
  Combobox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Modal,
  MultiCombobox,
  SegmentedControl,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
  Textarea,
  Tooltip,
} from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';

/* ── The server's own patterns, so a refusal is caught before it is sent ──── */

/**
 * ⛔ **THESE TWO DIFFER BY ONE CHARACTER AND MUST NEVER CONVERGE.**
 *
 * A single `PRICE_PATTERN` served `unitPrice` AND `discountAmount` in four files
 * in this repo — correct while both were non-negative, wrong the moment one
 * stopped being. Migration 171 lets a line PRICE be negative (a DEDUCTION LINE);
 * `discountAmount` stays non-negative under `ck_*_discount_amount_sign`.
 *
 * A negative in the first box is a **credit**. A negative in the second is a
 * **surcharge arriving through the discount field**, which the database refuses
 * only as errno 4025 — a 422 naming no field at all, which nobody can bind to the
 * control they typed in.
 */

/** `unitPrice`: signed. `^-?\d{1,10}(\.\d{1,2})?$`, straight off the POST schema. */
const PRICE_PATTERN = /^-?\d{1,10}(\.\d{1,2})?$/;

/** `discountAmount`: UNSIGNED — a discount is not a surcharge. */
const DISCOUNT_AMOUNT_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;

/** `discount` and `taxRate`: a PERCENTAGE 0..100. */
const PERCENT_PATTERN = /^\d{1,3}(\.\d{1,2})?$/;

/**
 * A tenant's Xero rate, as a string `taxRate` will actually accept.
 *
 * ⚠️ **The parameter is `number` and STAYS `number`.** `effectiveRate` is
 * `number | null`, `null` meaning Xero declined to state a percentage, and there
 * is no honest answer for it here — `?? 0` would write 0% tax onto a customer's
 * document off a figure that does not exist. `hasEffectiveRate()` narrows and
 * {@link lineTaxRates} withholds such rates from the picker.
 */
function percentForDraft(value: number): string {
  const exact = String(value);
  if (PERCENT_PATTERN.test(exact)) return exact;
  return value.toFixed(2);
}

/** `/products` caps here. */
const PRODUCTS_PER_PAGE = 100;

/** How many invoices the copy dialog's picker lists. The server's own ceiling. */
const INVOICES_PER_PAGE = 100;

/**
 * `description` caps here — the same 16,000 the invoice's own `terms` takes.
 *
 * Checked before sending rather than enforced with a `maxLength` on the box: a
 * `maxLength` truncates a PASTE silently, and the tail it drops is wording a
 * customer was meant to read.
 */
const MAX_DESCRIPTION_LENGTH = 16000;

/* ── The two pinned columns ───────────────────────────────────────────────── */

/*
 * ⚠️ **AMOUNT AND THE ACTIONS COLUMN ARE PINNED TO THE TRAILING EDGE.** The
 * `<colgroup>` sums to 1587 and the wrapper is around 1100, so without this the
 * figure the whole row exists to produce is the one thing never on screen.
 * Narrowing columns cannot close a ~300px shortfall.
 *
 * Three details make it work, and all three are the invoice grid's: the offset
 * equals the ACTIONS column's own width, the pinned cells must be OPAQUE, and the
 * leading hairline is an inset shadow because `border-collapse` would leave a
 * real border behind while the cell slid away from it.
 */
const PIN_AMOUNT = 'sticky right-[94px] z-10 shadow-[inset_1px_0_0_var(--hairline)]';
/** Hard against the trailing edge; the Amount column is offset by its width. */
const PIN_ACTIONS = 'sticky right-0 z-10';
/** An ordinary body row: the `Panel` surface the table sits on. */
const PIN_ROW_BG = 'bg-surface';
/**
 * A FAILED row's pinned cells. `bg-danger-soft/40` is translucent and a
 * translucent pinned cell is the defect the pins exist to fix, so the exact
 * composite is computed instead — alpha compositing is linear in sRGB, so this is
 * byte-for-byte what the row's own tint paints over `bg-surface`.
 */
const PIN_ROW_BG_FAILED = 'bg-[color-mix(in_srgb,var(--danger-soft)_40%,var(--surface))]';

/**
 * The number of columns, for the full-width rows under each line.
 *
 * ⚠️ Derived from nothing and written once, so it and the `<colgroup>` must move
 * together. A wrong value here draws a rule that stops short — it throws nothing.
 */
const COLUMN_COUNT = 9;

/* ── Props ────────────────────────────────────────────────────────────────── */

export interface CreditNoteLineItemsTableProps {
  creditNoteId: number;
  /**
   * The note's own currency code. **Never null** — every credit note is
   * denominated, and the column is `CHAR(3) NOT NULL`.
   *
   * ⚠️ **Every figure renders with it.** Nothing anywhere converts a currency, so
   * a figure without its code is a number a reader assumes is in theirs.
   */
  currency: string;
  /**
   * Whether this note's lines may be changed at all — the note's status, plus the
   * reader's permission, decided by the parent.
   *
   * ⚠️ **`false` DISABLES WITH A REASON, and never silently hides.** A bare
   * `disabled` makes a derived field, a permission denial and an ordinary value
   * indistinguishable.
   */
  editable: boolean;
  /**
   * Fired after any write the server accepted — a save run that landed at least
   * one row, a copy, or a delete.
   *
   * ⚠️ **The parent MUST re-read the credit note**: every line write re-derives
   * the note's four header totals server-side and none of the endpoints returns
   * the new header.
   */
  onChanged: () => void;
}

/* ── The row model ────────────────────────────────────────────────────────── */

/**
 * Three states, and collapsing any two of them puts a wrong figure on a
 * customer's document.
 *
 *  - `inherit` — the key is **OMITTED**, which is the only way to say "snapshot
 *    the catalogue price" on a create, and "leave it alone" on an update;
 *  - `value` — a real figure, **which may be negative**;
 *  - `none` — an explicit `null`: **no price on this line**, which is a different
 *    fact from a price of zero.
 *
 * ⛔ **Never send `''`, `null` or `0` to mean "use the catalogue".**
 */
type PriceMode = 'inherit' | 'value' | 'none';

interface Draft {
  productId: string;
  /**
   * What this line prints — **REQUIRED, and FROZEN**.
   *
   * ⛔ **`string`, never `string | null`.** There is no "derive it" to fall back
   * to, so there is no `null` to mean it and no reset control to offer. An empty
   * box is a refusal, not a clear.
   */
  description: string;
  quantity: string;
  priceMode: PriceMode;
  /** As typed. **May carry a leading `-`** — see {@link PRICE_PATTERN}. */
  price: string;
  /**
   * The discount as a PERCENTAGE, as typed. Live only while
   * {@link Draft.discountAmount} is `null`.
   */
  discount: string;
  /**
   * The discount as an EXACT MONEY AMOUNT, as typed — **and the MODE SWITCH**.
   *
   *  - `null` — the box is taking a PERCENTAGE, the ordinary case;
   *  - a string (`''` included) — the box is taking an AMOUNT.
   *
   * ⚠️ **NOTHING CONVERTS BETWEEN THE TWO**, and nothing may start. The
   * conversion this repo deleted was lossy by POUNDS: £1,000 off an £18,000 line
   * became `5.56%`, which takes £1,000.80.
   */
  discountAmount: string | null;
  taxRate: string;
  /**
   * Xero's tax CODE. `''` is `null`, which means **"use the company's Xero sales
   * default"** and never "no tax".
   */
  xeroTaxType: string;
  /**
   * The credited cities, as `f_cities` ids in string form (the MultiCombobox's
   * value type). Written as `cityIds`, a whole set, and only on a line linked
   * to an invoice line; a new (hand-typed) row never sends it.
   */
  cityIds: string[];
}

type RowState = 'idle' | 'saving' | 'saved' | 'failed';

interface Row {
  /** Stable across re-renders and independent of the server id, which new rows lack. */
  key: string;
  /** The last state the server confirmed. `null` on a row that has never been saved. */
  saved: CreditNoteLine | null;
  draft: Draft;
  state: RowState;
  error: string | null;
  fieldErrors: Record<string, string>;
  /**
   * Whether the price / the discount was typed by hand since this row loaded.
   * A city change re-prices only what was NOT typed (owner 2026-10-27).
   */
  priceTouched: boolean;
  discountTouched: boolean;
}

let nextRowKey = 0;
function freshKey(): string {
  nextRowKey += 1;
  return `new-${nextRowKey}`;
}

function draftOf(line: CreditNoteLine): Draft {
  return {
    productId: String(line.product.id),
    description: line.description,
    quantity: String(line.quantity),
    /*
     * ⚠️ A saved line with no price is `none`, not `inherit`. `inherit` on an
     * update means "leave it alone", so opening a priceless line in that mode
     * would look identical and mean something else the moment anything else on
     * the row changed.
     */
    priceMode: line.unitPrice === null ? 'none' : 'value',
    price: line.unitPrice ?? '',
    discount: line.discount,
    discountAmount: line.discountAmount,
    taxRate: line.taxRate,
    xeroTaxType: line.xeroTaxType ?? '',
    cityIds: citiesOfLine(line).map((city) => String(city.id)),
  };
}

/**
 * A credit line's cities. `undefined` is an API older than migration 331 and
 * reads as "no cities recorded", which is what that API stored.
 */
function citiesOfLine(line: CreditNoteLine): { id: number; name: string }[] {
  return line.cities ?? [];
}

/**
 * Cities already on a NON-VOID credit line for this invoice line. `undefined`
 * is an older API: nothing is known to be taken, and the server's 409 remains
 * the backstop.
 */
function creditedIdsOf(line: InvoiceProduct): ReadonlySet<number> {
  return new Set(line.creditedCityIds ?? []);
}

/**
 * The invoice line's cities a credit line may name, in the invoice line's own
 * order: every city not already credited elsewhere, plus `ownIds` (the credit
 * line's own cities count as available to it).
 */
function availableCitiesOf(
  line: InvoiceProduct,
  ownIds: readonly number[] = [],
): { id: number; name: string }[] {
  const credited = creditedIdsOf(line);
  const own = new Set(ownIds);
  return sourceCitiesOf(line).filter((city) => own.has(city.id) || !credited.has(city.id));
}

/**
 * The cities an invoice line really runs in: `effectiveCities` (the project's
 * cities for an all-project-cities line, which stores no city rows), else the
 * stored `cities` on an older API. The picker's source list and N in pro-rata.
 */
function sourceCitiesOf(line: InvoiceProduct): { id: number; name: string }[] {
  return line.effectiveCities ?? line.cities;
}

/** Why a line whose every city is already on a credit note cannot be ticked. */
const ALL_CITIES_CREDITED_REASON = 'Every city on this line has already been credited.';

/**
 * A line with cities, every one of them already on a non-void credit line —
 * nothing left to copy (the same rule as `CreditNoteFormModal`).
 */
function isFullyCredited(line: InvoiceProduct): boolean {
  return sourceCitiesOf(line).length > 0 && availableCitiesOf(line).length === 0;
}

function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((value) => set.has(value));
}

/**
 * The pro-rata unit price and discount amount for crediting `credited` of the
 * invoice line's cities, or `null` where no pro-rata figure exists (an
 * unpriced line, no cities chosen, or a count the rule refuses).
 */
function proRataFor(
  line: InvoiceProduct,
  credited: number,
): { unitPrice: string; discountAmount: string | null } | null {
  if (line.unitPrice === null || credited < 1) return null;
  try {
    return proRataCreditLine({
      unitPrice: line.unitPrice,
      discountAmount: line.discountAmount,
      lineCities: sourceCitiesOf(line).length,
      creditedCities: credited,
    });
  } catch (caught: unknown) {
    if (caught instanceof RangeError) return null;
    throw caught;
  }
}

/**
 * Whether the SAVED line's price / discount amount were typed by hand: they
 * differ from the pro-rata figure for its saved cities (N = the invoice line's
 * `effectiveCities ?? cities`). Seeds `priceTouched` / `discountTouched` once
 * the invoice-line lookup is ready, so a hand-edited price saved earlier is not
 * overwritten by a later city change. A saved line with no cities is compared
 * with the whole line; no pro-rata figure (an unpriced line) is "not typed".
 */
function savedHandEditsOf(
  saved: CreditNoteLine,
  line: InvoiceProduct,
): { price: boolean; discount: boolean } {
  const savedCount = citiesOfLine(saved).length;
  const expected = proRataFor(line, savedCount === 0 ? sourceCitiesOf(line).length : savedCount);
  if (expected === null) return { price: false, discount: false };
  return {
    price: saved.unitPrice !== expected.unitPrice,
    discount: saved.discountAmount !== expected.discountAmount,
  };
}

function blankDraft(): Draft {
  return {
    productId: '',
    description: '',
    quantity: '1',
    priceMode: 'inherit',
    price: '',
    discount: '',
    discountAmount: null,
    taxRate: '',
    xeroTaxType: '',
    cityIds: [],
  };
}

function rowOf(line: CreditNoteLine): Row {
  return {
    key: `line-${line.id}`,
    saved: line,
    draft: draftOf(line),
    state: 'idle',
    error: null,
    fieldErrors: {},
    priceTouched: false,
    discountTouched: false,
  };
}

/* ── Payloads ─────────────────────────────────────────────────────────────── */

interface DiscountWrite {
  /** A percentage, as typed. `''` means "say nothing about it". */
  discount: string;
  /** An exact amount, as typed, or `null` for "not discounted by amount". */
  discountAmount: string | null;
}

/**
 * Which of the two mutually exclusive discount columns a write carries.
 *
 * ⚠️ **Sending both non-zero is a 422**, so a write always carries the PAIR — the
 * live one, and the other said to be off.
 *
 * ⚠️ **An emptied AMOUNT box is a real zero, not a blank.** The percentage
 * control is not on screen in that mode, so "no amount" can only mean "no
 * discount on this line" — leaving a stored percentage standing would show an
 * empty discount box over a line that is still discounted.
 */
function discountWriteOf(draft: Draft): DiscountWrite {
  if (draft.discountAmount === null) {
    return { discount: draft.discount.trim(), discountAmount: null };
  }
  const amount = draft.discountAmount.trim();
  return { discount: '0', discountAmount: amount === '' ? null : amount };
}

/**
 * The POST body for a new line.
 *
 * ⚠️ **`description` is ALWAYS sent**, because it is required and there is
 * nothing to derive. {@link validateRow} refuses an empty one before this runs.
 *
 * ⚠️ **`unitPrice` is OMITTED under `inherit`**, and that omission is the whole
 * point of {@link PriceMode}.
 *
 * ⛔ **No `lineSubtotal`, `lineTax` or `lineTotal`** — generated columns, and
 * sending one is a 422. ⛔ **No `projectId`** — see the header.
 */
function createPayload(draft: Draft, sortOrder: number | undefined): CreditNoteLineCreateInput {
  const body: CreditNoteLineCreateInput = {
    productId: Number(draft.productId),
    description: draft.description.trim(),
  };
  if (sortOrder !== undefined) body.sortOrder = sortOrder;
  const quantity = draft.quantity.trim();
  if (quantity !== '') body.quantity = Number(quantity);
  if (draft.priceMode === 'value') body.unitPrice = draft.price.trim();
  else if (draft.priceMode === 'none') body.unitPrice = null;
  const money = discountWriteOf(draft);
  if (money.discount !== '') body.discount = money.discount;
  if (money.discountAmount !== null) body.discountAmount = money.discountAmount;
  const taxRate = draft.taxRate.trim();
  if (taxRate !== '') body.taxRate = taxRate;
  /*
   * ⚠️ `''` is OMITTED rather than sent. The server normalises `''` to `null`,
   * which is "the company's Xero sales default" — the same answer omission gives
   * on a create, with one fewer key to be wrong about.
   */
  if (draft.xeroTaxType !== '') body.xeroTaxType = draft.xeroTaxType;
  return body;
}

/**
 * The PATCH body: **only the keys that actually changed.**
 *
 * ⚠️ **An omitted key leaves the stored value alone; it does not reset it.** So a
 * spread of the loaded row would be harmless here and is still not done — the
 * diff is what keeps a save to one field from re-asserting five others a
 * colleague moved in the meantime.
 *
 * ⚠️ **`description` is sent only when it MOVED, and never as `null`.** It is the
 * one key on this resource where `null` is a 422 rather than a clear.
 */
function updatePayload(draft: Draft, saved: CreditNoteLine): CreditNoteLineUpdateInput {
  const base = draftOf(saved);
  const body: CreditNoteLineUpdateInput = {};
  if (draft.productId !== base.productId) body.productId = Number(draft.productId);
  const description = draft.description.trim();
  if (description !== base.description.trim()) body.description = description;
  const quantity = draft.quantity.trim();
  if (quantity !== '' && Number(quantity) !== saved.quantity) body.quantity = Number(quantity);
  /*
   * ⚠️ **`inherit` on an UPDATE means "leave the stored price alone"** — the key
   * is omitted, exactly as it is on a create. The other two modes are explicit,
   * and `none` sends a real `null`.
   */
  if (draft.priceMode === 'none') {
    if (saved.unitPrice !== null) body.unitPrice = null;
  } else if (draft.priceMode === 'value') {
    const price = draft.price.trim();
    if (price !== (saved.unitPrice ?? '')) body.unitPrice = price;
  }
  const money = discountWriteOf(draft);
  const baseMoney = discountWriteOf(base);
  /*
   * ⚠️ **The pair moves together or not at all.** The two columns are mutually
   * exclusive under a CHECK constraint, so a patch that set one without standing
   * the other down would be refused by the database with a message naming no
   * field a reader can bind to.
   */
  if (
    money.discountAmount !== baseMoney.discountAmount ||
    (money.discount !== '' && money.discount !== baseMoney.discount)
  ) {
    body.discountAmount = money.discountAmount;
    body.discount = money.discount === '' ? '0' : money.discount;
  }
  /*
   * ⚠️ **An emptied rate box sends `'0'`, not an omission.** `taxRate` is
   * `NOT NULL` with a default of 0, so there is no "unset" for it to return to —
   * and omitting the key would leave the stored percentage standing over an empty
   * box, which reads as a line carrying no tax while it still charges some.
   */
  const taxRate = draft.taxRate.trim();
  if (taxRate !== base.taxRate.trim()) body.taxRate = taxRate === '' ? '0' : taxRate;
  if (draft.xeroTaxType !== base.xeroTaxType) {
    body.xeroTaxType = draft.xeroTaxType === '' ? null : draft.xeroTaxType;
  }
  /*
   * ⚠️ **`cityIds` is a WHOLE SET** (absent = leave, `[]` = clear), sent only
   * when the set moved and only on a line linked to an invoice line — anything
   * else is a 422 `CREDIT_NOTE_CITIES_NEED_INVOICE_LINE`.
   *
   * ⚠️ **When it is sent, the price and the discount pair go with it,
   * explicitly.** The server re-prices a city write that carries no
   * `unitPrice`; stating the figure on screen (the pro-rata one this grid
   * computed, or the one somebody typed) keeps the server from replacing a
   * typed price that happens to equal the stored one.
   */
  if (saved.invoiceProductId !== null && !sameIdSet(draft.cityIds, base.cityIds)) {
    body.cityIds = draft.cityIds.map(Number);
    if (!('unitPrice' in body)) {
      if (draft.priceMode === 'none') body.unitPrice = null;
      else if (draft.priceMode === 'value') body.unitPrice = draft.price.trim();
      else body.unitPrice = saved.unitPrice;
    }
    if (!('discount' in body)) {
      body.discountAmount = money.discountAmount;
      body.discount = money.discount === '' ? '0' : money.discount;
    }
  }
  return body;
}

/**
 * Whether a row has anything to send.
 *
 * ⚠️ **A new row is always dirty** — it has never been written, so there is
 * nothing to diff it against and "unchanged" would mean "never saved".
 *
 * ⚠️ **An empty PATCH is a 422**, so this is what keeps a Save from firing a
 * request that can only be refused.
 */
function isDirty(row: Row): boolean {
  if (row.saved === null) return true;
  return Object.keys(updatePayload(row.draft, row.saved)).length > 0;
}

/* ── Validation ───────────────────────────────────────────────────────────── */

interface RowProblems {
  /** Local refusals and the server's own field errors, merged for RENDERING. */
  fieldErrors: Record<string, string>;
  error: string | null;
  /**
   * Whether this row may be sent at all.
   *
   * ⛔ **LOCAL VALIDATION ONLY — never the server's field errors.** A row the
   * server refused must stay retryable: a 409, a 500 or a refusal about a
   * neighbouring record would otherwise block the Save button permanently, with a
   * count reading "1 row needs attention" over a row nobody can fix from here.
   * Blocking is for values this client can prove wrong before sending them.
   */
  blocked: boolean;
}

function validateRow(row: Row): RowProblems {
  const fieldErrors: Record<string, string> = {};
  const draft = row.draft;
  if (draft.productId === '') fieldErrors.productId = 'Choose a product.';
  /*
   * ⛔ **THE ONE REFUSAL THAT DOES NOT EXIST ON THE OTHER THREE GRIDS.** There,
   * an empty description means DERIVE and is the ordinary case. Here the wording
   * is frozen and stored, so an empty box has nothing behind it — and `null` is a
   * 422 rather than a clear.
   *
   * Worded as what to do, not as a rule: "this field is required" over a box
   * somebody has just emptied names the rule and not the way out.
   */
  const description = draft.description.trim();
  if (description === '') {
    fieldErrors.description = 'Type what this line should print on the credit note.';
  } else if (description.length > MAX_DESCRIPTION_LENGTH) {
    fieldErrors.description = `At most ${MAX_DESCRIPTION_LENGTH.toLocaleString()} characters — this is ${description.length.toLocaleString()}.`;
  }
  const quantity = draft.quantity.trim();
  if (quantity !== '') {
    const parsed = Number(quantity);
    if (!Number.isInteger(parsed) || parsed < 1) {
      fieldErrors.quantity = 'A whole number of 1 or more.';
    }
  }
  if (draft.priceMode === 'value' && !PRICE_PATTERN.test(draft.price.trim())) {
    /*
     * ⚠️ **NO "cannot be negative" BRANCH, and that is deliberate.** A line price
     * MAY be negative since migration 171 — INV-0886 carries a real deduction
     * line at `-4150.00` — and the API's own pattern gained a `-?` in the same
     * change. The sentence therefore names decimal places and symbols and says
     * nothing about a sign.
     */
    const typed = draft.price.trim();
    fieldErrors.unitPrice =
      typed === ''
        ? 'Enter a price, or record the line as having none.'
        : 'Up to two decimal places, and no currency symbol. A negative price is a deduction.';
  }
  /*
   * ⚠️ **The DISCOUNT is checked in exactly one of its two shapes**, because only
   * one is on screen. Reporting the other's format would name a box that is not
   * there.
   */
  if (draft.discountAmount === null) {
    const trimmed = draft.discount.trim();
    // `Number()` compares a bound, never money. The value itself is sent as the
    // string the user typed, untouched by a float.
    if (trimmed !== '' && (!PERCENT_PATTERN.test(trimmed) || Number(trimmed) > 100)) {
      fieldErrors.discount = 'A percentage between 0 and 100.';
    }
  } else {
    const amount = draft.discountAmount.trim();
    if (amount !== '' && !DISCOUNT_AMOUNT_PATTERN.test(amount)) {
      /*
       * ⛔ **A discount may never be negative**, and typing `-500` here is the
       * natural way to say "take this off" now that a PRICE can carry a sign. The
       * database reports it only as errno 4025 — a 422 naming no field — so this
       * sentence is the only place a person learns what to use instead.
       */
      fieldErrors.discountAmount = amount.startsWith('-')
        ? 'A discount cannot be negative. To reduce a line below zero, use a negative price.'
        : 'Up to two decimal places, and no currency symbol.';
    }
  }
  const taxRate = draft.taxRate.trim();
  if (taxRate !== '' && (!PERCENT_PATTERN.test(taxRate) || Number(taxRate) > 100)) {
    fieldErrors.taxRate = 'A percentage between 0 and 100.';
  }
  const blocked = Object.keys(fieldErrors).length > 0;
  /*
   * ⚠️ **The server's errors are merged AFTER `blocked` is decided, and the
   * order is the whole point.** They must be visible on the control they name —
   * that is what routing them was for — and they must not latch the Save button.
   * The local sentence WINS a collision: it describes the value on screen now,
   * where the server's describes the one that was sent.
   */
  return {
    fieldErrors: { ...row.fieldErrors, ...fieldErrors },
    error: row.error,
    blocked,
  };
}

/** The keys a 422 can name, so `routeFieldErrors` can tell a bindable path from a stray one. */
const ROW_WRITE_KEYS: readonly string[] = [
  'productId',
  'description',
  'quantity',
  'unitPrice',
  'discount',
  'discountAmount',
  'taxRate',
  'xeroTaxType',
  'sortOrder',
  'cityIds',
];

function describeFailure(error: unknown): {
  message: string;
  fieldErrors: Record<string, string>;
} {
  // The city refusals are worded by CODE, in the one shared wording module.
  const cityRefusal = creditCityRefusalMessage(error);
  if (cityRefusal !== null) {
    // Bound to the Cities control on a row; the copy dialog shows `message`.
    return { message: cityRefusal, fieldErrors: { cityIds: cityRefusal } };
  }
  if (error instanceof ApiError) {
    /*
     * ⚠️ **Partitioned on this form's OWN write keys, not on the shape of the
     * path.** A 422 naming a real API key this row does not render would
     * otherwise route to an off-screen control: it vanishes from the banner,
     * inflates the "one field needs attention" count, and highlights nothing.
     */
    const { fields, unroutable } = error.routeFieldErrors(ROW_WRITE_KEYS);
    return {
      message: unroutable.length > 0 ? unroutable.join(' ') : error.message,
      fieldErrors: fields,
    };
  }
  return { message: 'The request did not reach the server.', fieldErrors: {} };
}

/* ── Reference data ───────────────────────────────────────────────────────── */

interface ProductRow {
  id: number;
  productName: string;
  productCode: string | null;
  /**
   * ⚠️ **`null` is UNPRICED and `"0.00"` is a real price of zero.** Never
   * `Number(price)`, never `price ?? 0` — the distinction is what the `inherit`
   * price mode snapshots.
   */
  unitPrice: string | null;
}

function useProducts() {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    api
      .listProducts({ perPage: PRODUCTS_PER_PAGE, active: true }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setRows(
          result.data.map((row) => ({
            id: row.id,
            productName: row.productName,
            productCode: row.productCode,
            unitPrice: row.unitPrice,
          })),
        );
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setFailed(true);
      });
    return () => controller.abort();
  }, []);
  return { rows, failed };
}

/* ── Presentational bits ──────────────────────────────────────────────────── */

function ErrorBlock({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Notice tone="danger" role="alert" title={title}>
      {children}
    </Notice>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
    </svg>
  );
}

/** The Price cell's "no price at all" toggle — a struck-through circle. */
function NoPriceIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <circle cx="12" cy="12" r="8" />
      <path d="m6.5 6.5 11 11" />
    </svg>
  );
}

function StatusBadge({ row, dirty }: { row: Row; dirty: boolean }) {
  if (row.state === 'saving') return <Badge tone="info">Saving…</Badge>;
  if (row.state === 'failed') return <Badge tone="danger">Not saved</Badge>;
  if (row.state === 'saved' && !dirty) return <Badge tone="success">Saved</Badge>;
  if (dirty) return <Badge tone="warning">{row.saved === null ? 'New' : 'Unsaved'}</Badge>;
  return <span className="text-xs text-fg-subtle">—</span>;
}

/**
 * ⚠️ **A `title` on a DISABLED `Button` NEVER RENDERS.** `Button` carries
 * `disabled:pointer-events-none`, so it never receives the hover — a
 * why-this-is-disabled sentence has to sit on a wrapper span. Found in this repo
 * at two call sites that had routed one to `title` and shipped it unreadable.
 *
 * ⚠️ SUPERSEDED IN PART 2026-09-24: `Button` now shows a disabled button's
 * `tooltip ?? title` itself, on a wrapper that receives the hover. This helper
 * is kept for the text buttons below, which carry no `title`; never wrap a
 * Button that has one, or it shows two labels.
 */
function DisabledReason({ reason, children }: { reason: string | null; children: ReactNode }) {
  if (reason === null) return <>{children}</>;
  return <span title={reason}>{children}</span>;
}

interface ControlProps {
  row: Row;
  problems: RowProblems;
  disabled: boolean;
  /**
   * The desktop grid. **Labels are hidden and hints move into `placeholder` or
   * `title`, or they go** — nothing explanatory renders inside a grid cell. The
   * stacked card below `md` is not a grid, so it keeps both.
   *
   * ⚠️ Two exceptions, because they are not prose: a field **error**, and a
   * `Combobox` **footnote**, which renders inside the popover.
   */
  compact: boolean;
  onChange: (patch: Partial<Draft>) => void;
}

function ProductControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  options,
  footnote,
}: ControlProps & { options: ComboboxOption[]; footnote?: string }) {
  const selectedLabel = options.find((option) => option.value === row.draft.productId)?.label;
  const control = (
    <Field label="Item" hideLabel={compact} error={problems.fieldErrors.productId}>
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={row.draft.productId}
          onChange={(value) => onChange({ productId: value })}
          options={options}
          placeholder="Choose a product"
          searchPlaceholder="Search products"
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.productId)}
          {...(footnote ? { footnote } : {})}
        />
      )}
    </Field>
  );
  // The trigger truncates under `table-fixed`, so the full name goes on a title.
  if (!compact || selectedLabel === undefined) return control;
  return <div title={selectedLabel}>{control}</div>;
}

/**
 * The wording this line prints — **required, frozen, and never derived.**
 *
 * ⛔ **There is no "Reset to derived" control here and there must not be one.**
 * On the quote, invoice and deal grids this cell types over a live derivation and
 * that button hands it back. A credit note says exactly what the invoice said,
 * resolved once at copy time by the same function the push uses, so there is
 * nothing to hand it back to — and `null` is a 422 rather than a clear.
 *
 * ⚠️ The hint says so in the stacked card, where prose is allowed. In the grid it
 * is a `placeholder`, which is the only channel a cell has.
 */
function DescriptionControl({ row, problems, disabled, compact, onChange }: ControlProps) {
  return (
    <Field
      label="Description"
      hideLabel={compact}
      required
      error={problems.fieldErrors.description}
      {...(compact
        ? {}
        : { hint: 'Printed on the credit note as typed. It is not derived and does not update.' })}
    >
      {(fieldProps) => (
        <Textarea
          {...fieldProps}
          value={row.draft.description}
          onChange={(event) => onChange({ description: event.target.value })}
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.description)}
          autoGrow={{ minRows: 2, maxRows: 8 }}
        />
      )}
    </Field>
  );
}

function QuantityControl({ row, problems, disabled, compact, onChange }: ControlProps) {
  return (
    <Field label="Qty" hideLabel={compact} error={problems.fieldErrors.quantity}>
      {(fieldProps) => (
        <Input
          {...fieldProps}
          inputMode="numeric"
          value={row.draft.quantity}
          onChange={(event) => onChange({ quantity: event.target.value })}
          placeholder="1"
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.quantity)}
          className="text-right tabular-nums"
        />
      )}
    </Field>
  );
}

function PriceControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  currency,
  catalogPrice,
}: ControlProps & { currency: string; catalogPrice: string | null | undefined }) {
  const mode = row.draft.priceMode;
  const isNew = row.saved === null;
  const noPrice = mode === 'none';
  const catalogueFigure =
    typeof catalogPrice === 'string' ? `${formatDecimalString(catalogPrice)} ${currency}` : null;
  const hint = noPrice
    ? 'No price at all — not a price of zero.'
    : mode === 'inherit'
      ? isNew
        ? catalogueFigure !== null
          ? `Blank uses the catalogue price, ${catalogueFigure}.`
          : catalogPrice === null
            ? 'No catalogue price, so blank leaves this line with none.'
            : 'Blank uses the catalogue price.'
        : `Blank leaves ${formatDecimalString(row.saved?.unitPrice ?? null)} unchanged.`
      : /*
         * ⚠️ **The sign is stated, because a credit note is where somebody needs
         * it.** A deduction line is a negative price, and nothing else on this
         * screen says the box will take one.
         */
        `In ${currency}. A negative figure is a deduction.`;
  const toggleLabel = noPrice
    ? 'Record a price on this line'
    : 'Record this line as carrying no price at all';
  return (
    <Field
      label="Price"
      hideLabel={compact}
      error={problems.fieldErrors.unitPrice}
      {...(!compact ? { hint } : {})}
    >
      {(fieldProps) => (
        <Input
          {...fieldProps}
          inputMode="decimal"
          value={noPrice ? '' : row.draft.price}
          onChange={(event) => {
            const next = event.target.value;
            /*
             * ⚠️ Emptying the box returns to `inherit` — the key is OMITTED, so a
             * new line snapshots the catalogue and an existing one keeps what it
             * has. It must never become `value` with an empty string.
             */
            onChange({ price: next, priceMode: next === '' ? 'inherit' : 'value' });
          }}
          placeholder={
            noPrice
              ? 'No price'
              : isNew
                ? typeof catalogPrice === 'string'
                  ? formatDecimalString(catalogPrice)
                  : catalogPrice === null
                    ? 'No catalogue price'
                    : 'Catalogue'
                : 'Unchanged'
          }
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.unitPrice)}
          className="text-right tabular-nums"
          title={hint}
          {...(disabled
            ? {}
            : {
                trailingSlot: (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={toggleLabel}
                    title={toggleLabel}
                    aria-pressed={noPrice}
                    onClick={() =>
                      onChange(
                        noPrice
                          ? { priceMode: 'inherit', price: '' }
                          : { priceMode: 'none', price: '' },
                      )
                    }
                  >
                    <NoPriceIcon />
                  </Button>
                ),
              })}
        />
      )}
    </Field>
  );
}

/**
 * One box, two columns, and a segmented control saying which is live.
 *
 * ⚠️ **NOTHING CONVERTS BETWEEN THEM.** The switch moves itself and leaves both
 * figures where they are; pre-filling one from the other is the deleted
 * conversion arriving from the other direction and would overwrite a figure
 * somebody typed with one nobody chose.
 */
function DiscountControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  currency,
}: ControlProps & { currency: string }) {
  const amountText = row.draft.discountAmount;
  const isAmount = amountText !== null;
  const error = isAmount ? problems.fieldErrors.discountAmount : problems.fieldErrors.discount;
  return (
    <div className="grid gap-1">
      <Field label="Discount" hideLabel={compact} error={error}>
        {(fieldProps) => (
          <Input
            {...fieldProps}
            inputMode="decimal"
            value={isAmount ? amountText : row.draft.discount}
            onChange={(event) =>
              onChange(
                isAmount
                  ? { discountAmount: event.target.value }
                  : { discount: event.target.value },
              )
            }
            placeholder={isAmount ? '0.00' : '0'}
            disabled={disabled}
            invalid={Boolean(error)}
            className="text-right tabular-nums"
            title={isAmount ? `An exact amount off, in ${currency}.` : 'A percentage off.'}
          />
        )}
      </Field>
      <SegmentedControl
        label={DISCOUNT_MODE_GROUP_LABEL}
        value={discountModeOf(amountText)}
        options={DISCOUNT_MODE_OPTIONS}
        size="sm"
        disabled={disabled}
        /*
         * ⚠️ Moves the switch and NOTHING else. `null` hands the box back to the
         * percentage; `''` opens an empty amount box, which the writer reads as
         * "no discount by amount" until somebody types in it.
         */
        onChange={(mode) => onChange({ discountAmount: mode === 'amount' ? '' : null })}
      />
    </div>
  );
}

/**
 * ⚠️ **ONE control, not two — it closes a real divergence.**
 *
 * `taxRate` and `xeroTaxType` feed two different systems and nothing reconciles
 * them: the Xero push sends **`TaxType` only** and Xero derives the money from
 * the code against its own rate table, while our `lineTax` is a generated column
 * computed from **`taxRate` and nothing else**. So a `tax_rate` of 5 beside a 0%
 * code prints tax here and bills none there, and the reverse bills tax against a
 * document showing none — **both documents look correct, on two screens, to two
 * people.**
 *
 * The picker therefore writes **both keys in the same patch**, from one rate
 * object. Nothing can set one alone.
 *
 * ⚠️ **The bare-`%` fallback is REQUIRED**, because the rate list is the
 * tenant's: there is nothing to list while Xero is disconnected, while the read
 * fails, or when the organisation lists none. `loading` is not a fallback — the
 * dropdown shows its own loading state.
 *
 * ⚠️ **No `taxRateSource` lock here, and it is inexpressible rather than
 * omitted.** {@link CreditNoteLine} carries no such field, so this grid cannot
 * know whether a rate came from the company or from the line and must not
 * pretend to.
 */
function TaxRateControl({
  row,
  problems,
  disabled,
  compact,
  onChange,
  rates,
  ratesLoading,
  ratesError,
  connected,
}: ControlProps & {
  rates: XeroTaxRate[];
  ratesLoading: boolean;
  ratesError: string | null;
  connected: boolean;
}) {
  const stored = row.draft.xeroTaxType;
  // Only the tax types set on the billing-region defaults (owner, 2026-10-12).
  const allowedTaxCodes = useDocumentTaxCodes();
  const unavailable = !connected || ratesError !== null || (!ratesLoading && rates.length === 0);
  if (unavailable) {
    const hint = [
      !connected
        ? 'Xero is not connected, so rates can’t be listed.'
        : (ratesError ?? 'This Xero organisation lists no tax rates.'),
      stored === ''
        ? 'This line uses the company’s Xero sales default for its tax code.'
        : `Xero tax code on this line: ${stored}`,
    ].join(' ');
    return (
      <Field
        label="Tax rate"
        hideLabel={compact}
        error={problems.fieldErrors.taxRate}
        {...(compact ? {} : { hint })}
      >
        {(fieldProps) => (
          <Input
            {...fieldProps}
            inputMode="decimal"
            value={row.draft.taxRate}
            onChange={(event) => onChange({ taxRate: event.target.value })}
            placeholder="0"
            disabled={disabled}
            invalid={Boolean(problems.fieldErrors.taxRate)}
            className="text-right tabular-nums"
            title={hint}
          />
        )}
      </Field>
    );
  }
  const offered = lineTaxRates(rates, stored, allowedTaxCodes);
  const options: ComboboxOption[] = offered.map((rate) => {
    if (!hasEffectiveRate(rate)) {
      return {
        value: rate.taxType,
        label: rate.name,
        hint: `${taxRateHint(rate)} · already on this line — Xero publishes no percentage for it, so choosing it here would record no rate; choose a rate below`,
      };
    }
    const rounded = percentForDraft(rate.effectiveRate);
    const rephrased = rounded !== String(rate.effectiveRate);
    const hint = rephrased
      ? `${taxRateHint(rate)} · recorded here as ${rounded}%`
      : taxRateHint(rate);
    if (!isSalesTaxRate(rate)) {
      return {
        value: rate.taxType,
        label: rate.name,
        hint: `${hint} · already on this line — Xero refuses an expense rate on a sales document, so choose a sales rate below`,
      };
    }
    return { value: rate.taxType, label: rate.name, hint };
  });
  /*
   * A stored code Xero no longer lists still has to be selectable, or the row
   * cannot be saved without silently changing its tax treatment.
   */
  if (stored !== '' && !options.some((option) => sameTaxCode(option.value, stored))) {
    options.unshift({ value: stored, label: stored, hint: 'no longer listed by Xero' });
  }
  return (
    <Field label="Tax rate" hideLabel={compact} error={problems.fieldErrors.taxRate}>
      {(fieldProps) => (
        <Combobox
          {...fieldProps}
          value={stored}
          onChange={(value) => {
            const rate = offered.find((candidate) => sameTaxCode(candidate.taxType, value));
            /*
             * ⛔ **BOTH KEYS, ONE PATCH.** A rate Xero publishes no percentage for
             * leaves `taxRate` alone rather than writing a `0` nobody stated —
             * `?? 0` here is the one edit that reintroduces the divergence this
             * control exists to close.
             */
            if (rate !== undefined && hasEffectiveRate(rate)) {
              onChange({ xeroTaxType: value, taxRate: percentForDraft(rate.effectiveRate) });
            } else {
              onChange({ xeroTaxType: value });
            }
          }}
          options={options}
          /*
           * ⚠️ `''` is a real value meaning "use the company's Xero sales
           * default" — never "no tax", and never an absence.
           */
          emptyOptionLabel="Company’s Xero sales default"
          placeholder="Company’s Xero sales default"
          searchPlaceholder="Search rates"
          loading={ratesLoading}
          disabled={disabled}
          invalid={Boolean(problems.fieldErrors.taxRate)}
        />
      )}
    </Field>
  );
}

/**
 * The line's money, as a READOUT.
 *
 * ⛔ **Never computed by hand.** A clean saved row shows the server's own
 * `lineTotal`; a dirty or new row shows {@link computeGeneratedLinePreview},
 * which repeats the database's rounding in the database's order — tax on the
 * already-rounded subtotal, the total as the sum of two rounded parts.
 *
 * ⚠️ **It is NOT editable here.** The invoice grid solves a typed amount back
 * into a price or a discount (`solveLineAmount`); that is a separate owner
 * decision and this grid does not have it. Recorded as a decision rather than an
 * omission, so a later pass does not close it by symmetry.
 *
 * ⚠️ **A negative figure renders as a negative figure.** No clamp, no
 * `Math.abs`, no parentheses convention invented here.
 */
function AmountCell({
  row,
  preview,
  currency,
}: {
  row: Row;
  preview: GeneratedLinePreview | null;
  currency: string;
}) {
  const dirty = isDirty(row);
  const total = !dirty && row.saved !== null ? row.saved.lineTotal : (preview?.lineTotal ?? null);
  const subtotal =
    !dirty && row.saved !== null ? row.saved.lineSubtotal : (preview?.lineSubtotal ?? null);
  const differs = total !== null && subtotal !== null && total !== subtotal;
  return (
    <div className="grid justify-items-end gap-0.5">
      <Money amount={total} currency={currency} className="text-sm text-fg" />
      {differs && (
        <span className="text-2xs text-fg-muted">{formatDecimalString(subtotal)} before tax</span>
      )}
    </div>
  );
}

/* ── Cities (owner 2026-10-27) ────────────────────────────────────────────── */

function CityChips({ names }: { names: readonly string[] }) {
  if (names.length === 0) return <span className="text-xs text-fg-subtle">—</span>;
  return (
    <div className="flex min-w-0 flex-wrap gap-1">
      {names.map((name, index) => (
        <Badge key={`${index}-${name}`} tone="neutral">
          {name}
        </Badge>
      ))}
    </div>
  );
}

/** Where the invoice line behind a row stands, for the Cities cell. */
type InvoiceLineLookup =
  | { state: 'none' }
  | { state: 'loading' }
  | { state: 'failed' }
  | { state: 'ready'; line: InvoiceProduct };

/**
 * The credited cities of one credit line.
 *
 * A picker only on an EDITABLE note, for a SAVED line linked to an invoice line
 * whose cities have been read. Everything else is read-only chips: an
 * approved or void note, a hand-typed line (no invoice line, so no cities), or
 * a new row. A whole-line credit shows every city with no suffix; a partial
 * credit shows the suffix Xero appends to its description.
 */
function CitiesControl({
  row,
  problems,
  disabled,
  compact,
  pickable,
  lookup,
  onCitiesChange,
}: Omit<ControlProps, 'onChange'> & {
  /** The note is editable (status and permission). */
  pickable: boolean;
  lookup: InvoiceLineLookup;
  onCitiesChange: (values: string[], invoiceLine: InvoiceProduct) => void;
}) {
  const saved = row.saved;
  const savedCities = saved === null ? [] : citiesOfLine(saved);
  const error = problems.fieldErrors.cityIds;
  const linked = saved !== null && saved.invoiceProductId !== null;

  if (!pickable || !linked || lookup.state === 'none') {
    const names = savedCities.map((city) => city.name);
    return (
      <Field label="Cities" hideLabel={compact} error={error}>
        {() => (
          <div className="grid min-w-0 gap-1">
            <CityChips names={names} />
          </div>
        )}
      </Field>
    );
  }

  if (lookup.state === 'loading') {
    return <Skeleton className="h-9 w-full" />;
  }

  if (lookup.state === 'failed') {
    return (
      <Field label="Cities" hideLabel={compact} error={error}>
        {() => (
          <div className="grid min-w-0 gap-1">
            <CityChips names={savedCities.map((city) => city.name)} />
            <span className="text-2xs text-danger">
              The invoice line could not be read, so its cities can’t be changed here.
            </span>
          </div>
        )}
      </Field>
    );
  }

  const invoiceLine = lookup.line;
  const ownIds = savedCities.map((city) => city.id);
  const available = availableCitiesOf(invoiceLine, ownIds);
  const lineCities = sourceCitiesOf(invoiceLine);
  const options: { value: string; label: string; hint?: string }[] = available.map((city) => ({
    value: String(city.id),
    label: city.name,
  }));
  // A stored city the invoice line no longer lists stays selectable, or the
  // row could not be saved without silently dropping it.
  for (const city of savedCities) {
    if (!options.some((option) => option.value === String(city.id))) {
      options.push({ value: String(city.id), label: city.name, hint: 'not on the invoice line' });
    }
  }
  const selected = new Set(row.draft.cityIds);
  const selectedNames = [
    ...lineCities.filter((city) => selected.has(String(city.id))).map((city) => city.name),
    ...savedCities
      .filter(
        (city) =>
          selected.has(String(city.id)) &&
          !lineCities.some((candidate) => candidate.id === city.id),
      )
      .map((city) => city.name),
  ];
  return (
    <Field label="Cities" hideLabel={compact} error={error}>
      {(fieldProps) => (
        <div className="grid min-w-0 gap-1" title={compact ? selectedNames.join(', ') : undefined}>
          <MultiCombobox
            {...fieldProps}
            values={row.draft.cityIds}
            onChange={(values) => onCitiesChange(values, invoiceLine)}
            options={options}
            placeholder="No cities"
            searchPlaceholder="Search cities"
            summaryNoun={{ singular: 'city', plural: 'cities' }}
            {...(compact ? { maxVisibleChips: 0 } : {})}
            selectAll
            disabled={disabled}
            invalid={Boolean(error)}
          />
        </div>
      )}
    </Field>
  );
}

interface CreditCitiesChoiceProps {
  /** The invoice line being credited. Renders nothing when it has no cities. */
  line: InvoiceProduct;
  /**
   * The chosen city ids, or `null` for untouched — every city not already on
   * another credit note ({@link copyCitiesOf} applies the same default).
   */
  selected: string[] | null;
  onChange: (next: string[]) => void;
  /** The credit note's currency, for the pro-rata figure. */
  currency: string;
  disabled?: boolean;
}

/**
 * The city picker on a COPY of an invoice line in {@link CopyFromInvoiceDialog}
 * (the raise flow's cities picker lives in `CreditNoteFormModal`). All
 * available cities by default, which is a whole-line credit as before; a subset
 * shows the pro-rata credit live.
 */
function CreditCitiesChoice({
  line,
  selected,
  onChange,
  currency,
  disabled = false,
}: CreditCitiesChoiceProps) {
  const lineCityCount = sourceCitiesOf(line).length;
  if (lineCityCount === 0) return null;
  const available = availableCitiesOf(line);
  if (available.length === 0) {
    return (
      <p className="text-xs text-danger">
        Every city on this line is already on another credit note.
      </p>
    );
  }
  const chosen = selected ?? available.map((city) => String(city.id));
  const proRata =
    chosen.length > 0 && chosen.length < lineCityCount
      ? proRataFor(line, chosen.length)
      : null;
  const preview =
    proRata === null
      ? null
      : computeGeneratedLinePreview({
          quantity: String(line.quantity),
          unitPrice: proRata.unitPrice,
          discount: line.discount,
          discountAmount: proRata.discountAmount,
          taxRate: line.taxRate,
        });
  return (
    <div className="grid min-w-0 gap-1">
      <Field
        label="Cities"
        error={chosen.length === 0 ? 'Choose at least one city, or untick the line.' : undefined}
      >
        {(fieldProps) => (
          <MultiCombobox
            {...fieldProps}
            values={chosen}
            onChange={onChange}
            options={available.map((city) => ({
              value: String(city.id),
              label: city.name,
            }))}
            placeholder="No cities"
            searchPlaceholder="Search cities"
            summaryNoun={{ singular: 'city', plural: 'cities' }}
            selectAll
            disabled={disabled}
            invalid={chosen.length === 0}
          />
        )}
      </Field>
      {preview !== null && (
        <span className="text-xs tabular-nums text-fg-subtle">
          {chosen.length} of {lineCityCount} cities ·{' '}
          {formatDecimalString(preview.lineTotal)} {currency}
        </span>
      )}
    </div>
  );
}

/**
 * The `cities` entry a copy sends for one invoice line, or `undefined` for a
 * whole-line copy (every city, the server's default). `blocked` when the line
 * cannot be copied as chosen: no city left, or none chosen.
 */
function copyCitiesOf(
  line: InvoiceProduct,
  selected: string[] | null,
): { entry?: { invoiceProductId: number; cityIds: number[] }; blocked: boolean } {
  const lineCityCount = sourceCitiesOf(line).length;
  if (lineCityCount === 0) return { blocked: false };
  const chosen = selected ?? availableCitiesOf(line).map((city) => String(city.id));
  if (chosen.length === 0) return { blocked: true };
  if (chosen.length === lineCityCount) return { blocked: false };
  return { entry: { invoiceProductId: line.id, cityIds: chosen.map(Number) }, blocked: false };
}

/* ── Copy from an invoice ─────────────────────────────────────────────────── */

/**
 * Pick an invoice, pick its lines, copy them onto this note in ONE request.
 *
 * ⛔ **`[]` is a 422, not "none".** An empty selection is refused here, with a
 * sentence, and never sent.
 *
 * ⛔ **Omitting `invoiceProductIds` means EVERY line on that invoice, AS THE
 * SERVER SEES IT NOW.** So the key is omitted only where the lines could not be
 * enumerated at all — that is the one case where "all of them, unseen" is what a
 * person is actually being offered, and it says so. Where the list loaded, the
 * ticked ids are sent explicitly, so what is copied is exactly what was on
 * screen.
 *
 * ⚠️ **The picker is scoped to this note's company and currency where it can
 * be.** Both are the server's refusal conditions, so narrowing the offer is what
 * keeps the 422 a backstop; where the note read fails the list is unscoped and
 * the server's sentence — which names `invoiceId` — is rendered verbatim.
 */
function CopyFromInvoiceDialog({
  open,
  onClose,
  creditNoteId,
  currency,
  onCopied,
}: {
  open: boolean;
  onClose: () => void;
  creditNoteId: number;
  currency: string;
  onCopied: (lines: CreditNoteLine[]) => void;
}) {
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [scopeFailed, setScopeFailed] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [invoiceId, setInvoiceId] = useState('');
  const [lines, setLines] = useState<InvoiceProduct[] | null>(null);
  const [linesError, setLinesError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  /** Chosen cities per invoice line id; absent = every available city. */
  const [cityChoice, setCityChoice] = useState<Map<number, string[]>>(new Map());
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosenLines = (lines ?? []).filter((line) => selected.has(line.id));
  const cityPlans = chosenLines.map((line) => copyCitiesOf(line, cityChoice.get(line.id) ?? null));
  const citiesBlocked = cityPlans.some((plan) => plan.blocked);

  /*
   * ⚠️ **The note is read ONCE, on open, and only for its company id.** The
   * parent already holds the note; this component's props deliberately do not
   * carry it, so this is the cheapest honest way to narrow the offer. A failure
   * is not fatal — it drops the scope and lets the server refuse.
   */
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    api
      .getCreditNote(creditNoteId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setCompanyId(result.data.company.id);
        setScopeFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setScopeFailed(true);
      });
    return () => controller.abort();
  }, [open, creditNoteId]);

  useEffect(() => {
    if (!open) return;
    if (companyId === null && !scopeFailed) return;
    const controller = new AbortController();
    setInvoices(null);
    setInvoicesError(null);
    api
      .listInvoices(
        {
          perPage: INVOICES_PER_PAGE,
          sort: 'id',
          order: 'desc',
          ...(companyId === null ? {} : { companyId }),
          /*
           * ⚠️ The second refusal condition, narrowed the same way. A credit note
           * and the invoice it credits must agree on currency; offering one that
           * cannot match is offering a 422.
           */
          currency,
        },
        controller.signal,
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setInvoices(result.data);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setInvoicesError(describeFailure(caught).message);
      });
    return () => controller.abort();
  }, [open, companyId, scopeFailed, currency]);

  useEffect(() => {
    if (invoiceId === '') {
      setLines(null);
      setLinesError(null);
      setSelected(new Set());
      return;
    }
    const controller = new AbortController();
    setLines(null);
    setLinesError(null);
    api
      .listInvoiceProducts(Number(invoiceId), controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setLines(result.data);
        // Everything ticked: crediting the whole invoice is the common case.
        // A line with nothing left to credit is never ticked.
        setSelected(
          new Set(result.data.filter((line) => !isFullyCredited(line)).map((line) => line.id)),
        );
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setLinesError(describeFailure(caught).message);
      });
    return () => controller.abort();
  }, [invoiceId]);

  function reset() {
    setInvoiceId('');
    setLines(null);
    setLinesError(null);
    setSelected(new Set());
    setCityChoice(new Map());
    setError(null);
  }

  async function copy() {
    if (invoiceId === '') return;
    /*
     * ⚠️ **Guarded here as well as on the button.** The omit-the-key branch below
     * means "every line on this invoice, unseen", and it is only honest once the
     * read has FAILED — a contract enforced solely by a disabled control is
     * enforced by nothing.
     */
    if (lines === null && linesError === null) return;
    // The refusal the endpoint would otherwise answer, caught before it is sent.
    if (lines !== null && selected.size === 0) {
      setError('Tick at least one line to credit.');
      return;
    }
    if (citiesBlocked) return;
    setCopying(true);
    setError(null);
    try {
      const cityEntries = cityPlans.flatMap((plan) => (plan.entry ? [plan.entry] : []));
      const body: CopyInvoiceLinesInput =
        lines === null
          ? /*
             * The lines could not be listed, so there are no ids to send and
             * omission is the only expressible request: every line on that
             * invoice. The button beside this says exactly that.
             */
            { invoiceId: Number(invoiceId) }
          : {
              invoiceId: Number(invoiceId),
              invoiceProductIds: chosenLines.map((line) => line.id),
            };
      // Only lines crediting a SUBSET are named; every other line is whole.
      if (cityEntries.length > 0) body.cities = cityEntries;
      const result = await api.copyInvoiceLinesToCreditNote(creditNoteId, body);
      /*
       * ⚠️ **An ARRAY under `data`, unlike every other create on this resource.**
       * A caller reading `data.id` gets `undefined` rather than an error, which
       * renders as a line that saved and then cannot be found.
       */
      onCopied(result.data);
      reset();
      onClose();
    } catch (caught: unknown) {
      /*
       * ⚠️ **A mismatched company or currency is a 422 naming `invoiceId`.**
       * Rendered verbatim: the server's sentence says which coordinate
       * disagreed, and nothing of ours can say it better.
       */
      setError(describeFailure(caught).message);
    } finally {
      setCopying(false);
    }
  }

  const invoiceOptions: ComboboxOption[] = (invoices ?? []).map((invoice) => ({
    value: String(invoice.id),
    label: invoice.subject,
    hint: [
      invoice.xeroInvoiceNumber,
      `${formatDecimalString(invoice.grandTotal)} ${invoice.currency ?? currency}`,
      invoice.billDate,
    ]
      .filter((part): part is string => typeof part === 'string' && part !== '')
      .join(' · '),
  }));

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Copy lines from an invoice"
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={copying}
            disabled={
              invoiceId === '' ||
              // Still reading the lines: there is nothing yet to copy OR to name.
              (lines === null && linesError === null) ||
              (lines !== null && selected.size === 0) ||
              citiesBlocked
            }
            onClick={() => void copy()}
          >
            {/*
              ⚠️ **The unseen-copy label is gated on the read having FAILED, not on
              the list being absent.** `lines === null` is also true while the
              fetch is in flight, so labelling it there promises "every line on
              this invoice" for the second before the list arrives — and a person
              who presses in that second gets exactly what it said, which is not
              what they were about to be shown.
            */}
            {linesError !== null
              ? 'Copy every line on this invoice'
              : `Copy ${selected.size} line${selected.size === 1 ? '' : 's'}`}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        {/*
          ⚠️ **NOT helper text — it names a state the reader cannot otherwise
          see.** The copy is a snapshot and the wording it takes is frozen, so a
          person who reads this as a live link will expect a correction to the
          invoice to follow through to the credit note. It never will.
        */}
        <p className="text-xs text-fg-muted">
          Each line is copied as it stands now, including its wording. Editing the invoice
          afterwards does not change this credit note.
        </p>
        {scopeFailed && (
          <p className="text-xs text-warning">
            The credit note’s account could not be read, so every invoice in {currency} is listed.
            One on another account will be refused.
          </p>
        )}
        {invoicesError !== null && <ErrorBlock title={invoicesError} />}
        <Field label="Invoice">
          {(fieldProps) => (
            <Combobox
              {...fieldProps}
              value={invoiceId}
              onChange={(next) => {
                setInvoiceId(next);
                // City choices belong to the previous invoice's lines.
                setCityChoice(new Map());
              }}
              options={invoiceOptions}
              placeholder="Choose an invoice"
              searchPlaceholder="Search invoices"
              loading={invoices === null && invoicesError === null}
              {...(invoices !== null && invoices.length === INVOICES_PER_PAGE
                ? { footnote: `Showing the first ${INVOICES_PER_PAGE} invoices.` }
                : {})}
            />
          )}
        </Field>
        {invoiceId !== '' && linesError !== null && (
          <ErrorBlock title={linesError}>
            <p>
              Its lines could not be listed, so they cannot be picked over. Copying will take every
              line on that invoice.
            </p>
          </ErrorBlock>
        )}
        {invoiceId !== '' && linesError === null && lines === null && (
          <Skeleton className="h-24 w-full" />
        )}
        {lines !== null && lines.length === 0 && (
          <p className="text-xs text-fg-muted">That invoice has no line items.</p>
        )}
        {lines !== null && lines.length > 0 && (
          <div className="grid gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs font-medium text-fg-secondary">Lines to credit</span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setSelected(
                      new Set(
                        lines.filter((line) => !isFullyCredited(line)).map((line) => line.id),
                      ),
                    )
                  }
                >
                  Select all
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
              </div>
            </div>
            <ul className="grid gap-1">
              {lines.map((line) => {
                const credited = isFullyCredited(line);
                const label = (
                  <span className="grid gap-0.5">
                    <span className="text-sm text-fg">{line.product.productName}</span>
                    {line.description !== null && (
                      <span className="text-xs text-fg-muted">{line.description}</span>
                    )}
                    <span className="text-xs tabular-nums text-fg-subtle">
                      {line.quantity} × {formatDecimalString(line.unitPrice)} ={' '}
                      {formatDecimalString(line.lineTotal)} {currency}
                    </span>
                  </span>
                );
                return (
                  <li key={line.id} className="rounded-md border border-border px-3 py-2">
                    {/*
                      A line whose every city is already credited is listed,
                      unticked and disabled, with its reason on a WRAPPER (a
                      disabled control receives no hover) and as a visible line.
                    */}
                    {credited ? (
                      <div className="grid gap-1">
                        <Tooltip label={ALL_CITIES_CREDITED_REASON} className="block">
                          <span className="block">
                            <Checkbox checked={false} disabled onChange={() => {}} label={label} />
                          </span>
                        </Tooltip>
                        <p className="text-xs text-fg-muted">{ALL_CITIES_CREDITED_REASON}</p>
                      </div>
                    ) : (
                      <Checkbox
                        checked={selected.has(line.id)}
                        onChange={(event) => {
                          const next = new Set(selected);
                          if (event.target.checked) next.add(line.id);
                          else next.delete(line.id);
                          setSelected(next);
                        }}
                        label={label}
                      />
                    )}
                    {!credited && selected.has(line.id) && sourceCitiesOf(line).length > 0 && (
                      <div className="mt-2 pl-6">
                        <CreditCitiesChoice
                          line={line}
                          selected={cityChoice.get(line.id) ?? null}
                          onChange={(next) =>
                            setCityChoice((current) => new Map(current).set(line.id, next))
                          }
                          currency={currency}
                          disabled={copying}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            {selected.size === 0 && (
              // The refusal, said where the choice is made rather than after a
              // request that would have been refused anyway.
              <p className="text-xs text-warning">
                {lines.every(isFullyCredited)
                  ? 'Every line on this invoice has already been credited.'
                  : 'Tick at least one line to credit.'}
              </p>
            )}
          </div>
        )}
        {error !== null && <ErrorBlock title={error} />}
      </div>
    </Modal>
  );
}

/* ── The grid ─────────────────────────────────────────────────────────────── */

export function CreditNoteLineItemsTable({
  creditNoteId,
  currency,
  editable,
  onChanged,
}: CreditNoteLineItemsTableProps) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState<{ saved: number; failed: number } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);

  const products = useProducts();
  const xero = useXero();
  const xeroConnected = !xero.loading && xero.connection.connected;
  const taxRates = useXeroTaxRates(xeroConnected);

  const load = useCallback(
    (signal: AbortSignal) => {
      setLoading(true);
      setLoadError(null);
      api
        .listCreditNoteLines(creditNoteId, signal)
        .then((result) => {
          if (signal.aborted) return;
          setRows(result.data.map(rowOf));
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          setLoadError(describeFailure(caught).message);
          setRows([]);
        })
        .finally(() => {
          if (signal.aborted) return;
          setLoading(false);
        });
    },
    [creditNoteId],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /*
   * The source invoice lines behind the linked rows, read only while the note
   * is editable (the picker needs their cities and `creditedCityIds`). One
   * read per distinct invoice, keyed by the sorted id list so a copy from a new
   * invoice reads it, and re-read on `invoiceLinesReload` after any save that
   * sent `cityIds` and after a copy, so `creditedCityIds` is never stale.
   */
  const linkedInvoiceKey = useMemo(() => {
    const ids = new Set<number>();
    for (const row of rows) {
      if (row.saved !== null && row.saved.invoiceProductId !== null && row.saved.invoice !== null) {
        ids.add(row.saved.invoice.id);
      }
    }
    return [...ids].sort((a, b) => a - b).join(',');
  }, [rows]);
  const [invoiceLines, setInvoiceLines] = useState<{
    key: string;
    byLineId: Map<number, InvoiceProduct>;
    failedInvoiceIds: Set<number>;
  } | null>(null);
  const [invoiceLinesReload, setInvoiceLinesReload] = useState(0);

  useEffect(() => {
    if (!editable || linkedInvoiceKey === '') return;
    const controller = new AbortController();
    const invoiceIds = linkedInvoiceKey.split(',').map(Number);
    void Promise.allSettled(
      invoiceIds.map((id) => api.listInvoiceProducts(id, controller.signal)),
    ).then((results) => {
      if (controller.signal.aborted) return;
      const byLineId = new Map<number, InvoiceProduct>();
      const failedInvoiceIds = new Set<number>();
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          for (const line of result.value.data) byLineId.set(line.id, line);
        } else {
          failedInvoiceIds.add(invoiceIds[index] ?? -1);
        }
      });
      setInvoiceLines({ key: linkedInvoiceKey, byLineId, failedInvoiceIds });
    });
    return () => controller.abort();
  }, [editable, linkedInvoiceKey, invoiceLinesReload]);

  function lookupFor(row: Row): InvoiceLineLookup {
    const saved = row.saved;
    if (!editable || saved === null || saved.invoiceProductId === null || saved.invoice === null) {
      return { state: 'none' };
    }
    if (invoiceLines === null || invoiceLines.key !== linkedInvoiceKey) return { state: 'loading' };
    const line = invoiceLines.byLineId.get(saved.invoiceProductId);
    if (line !== undefined) return { state: 'ready', line };
    return { state: 'failed' };
  }

  const productOptions: ComboboxOption[] = useMemo(
    () =>
      products.rows.map((product) => ({
        value: String(product.id),
        label: product.productName,
        /*
         * ⚠️ **The catalogue AMOUNT, not the word "catalogue".** A price is the
         * thing the reader is checking; a label naming a concept sends them to
         * the product record to see a number this row already knows.
         */
        hint: [
          product.productCode,
          product.unitPrice === null
            ? 'no catalogue price'
            : `${formatDecimalString(product.unitPrice)} ${currency}`,
        ]
          .filter((part): part is string => typeof part === 'string' && part !== '')
          .join(' · '),
      })),
    [products.rows, currency],
  );

  const problemsFor = useCallback((row: Row) => validateRow(row), []);

  const dirtyKeys = useMemo(
    () => new Set(rows.filter((row) => isDirty(row)).map((row) => row.key)),
    [rows],
  );
  const blockedKeys = useMemo(
    () => new Set(rows.filter((row) => problemsFor(row).blocked).map((row) => row.key)),
    [rows, problemsFor],
  );
  const dirtyCount = dirtyKeys.size;

  /**
   * The total of what the SERVER has stored, never of what is on screen.
   *
   * ⚠️ **It is not the note's header total and never has been.** The note's four
   * figures are re-derived server-side on every line write; this is a cross-check
   * over `lineTotal` values, labelled as one — a locally-summed figure presented
   * as the document's own is how a wrong number reaches a customer.
   */
  const savedTotal = useMemo(
    () => sumDecimalStrings(rows.map((row) => row.saved?.lineTotal ?? null)),
    [rows],
  );

  function catalogPriceOf(row: Row): string | null | undefined {
    if (row.draft.productId === '') return undefined;
    const product = products.rows.find((candidate) => String(candidate.id) === row.draft.productId);
    // `undefined` is "not read", `null` is "read, and it has no price". Different
    // facts, and the Price control words them differently.
    return product === undefined ? undefined : product.unitPrice;
  }

  function previewFor(row: Row): GeneratedLinePreview | null {
    const catalogue = catalogPriceOf(row);
    const resolvedPrice =
      row.draft.priceMode === 'none'
        ? null
        : row.draft.priceMode === 'value'
          ? row.draft.price.trim()
          : (row.saved?.unitPrice ?? (typeof catalogue === 'string' ? catalogue : null));
    const money = discountWriteOf(row.draft);
    return computeGeneratedLinePreview({
      quantity: row.draft.quantity.trim() === '' ? '1' : row.draft.quantity,
      unitPrice: resolvedPrice,
      discount: money.discount,
      discountAmount: money.discountAmount,
      taxRate: row.draft.taxRate,
    });
  }

  /**
   * A person's edit. `touched` is false only for the city re-price below, so a
   * figure this grid computed never counts as typed.
   */
  function patchRow(key: string, patch: Partial<Draft>, touched = true) {
    setRows((current) =>
      current.map((row) =>
        row.key === key
          ? {
              ...row,
              draft: { ...row.draft, ...patch },
              // A row that failed is no longer failing once it is edited; the
              // server's own field errors go with the values they described.
              state: row.state === 'failed' ? 'idle' : row.state,
              error: null,
              fieldErrors: {},
              priceTouched:
                row.priceTouched || (touched && ('price' in patch || 'priceMode' in patch)),
              discountTouched:
                row.discountTouched ||
                (touched && ('discount' in patch || 'discountAmount' in patch)),
            }
          : row,
      ),
    );
  }

  /**
   * A city change. Re-prices the line pro rata from the invoice line
   * (`proRataCreditLine`), but only the figures nobody typed since load: a
   * typed price, or a typed discount, is kept as typed.
   */
  function patchCities(row: Row, values: string[], invoiceLine: InvoiceProduct) {
    const patch: Partial<Draft> = { cityIds: values };
    /*
     * ⚠️ `rowOf` cannot know whether a SAVED figure was typed (it has no
     * invoice line), so the seed is taken here, where the lookup is ready: a
     * saved price that is not the pro-rata figure for its saved cities was
     * typed, and is kept.
     */
    const seeded =
      row.saved === null ? { price: false, discount: false } : savedHandEditsOf(row.saved, invoiceLine);
    const priceTouched = row.priceTouched || seeded.price;
    const discountTouched = row.discountTouched || seeded.discount;
    const proRata = proRataFor(invoiceLine, values.length);
    if (proRata !== null) {
      if (!priceTouched) {
        patch.priceMode = 'value';
        patch.price = proRata.unitPrice;
      }
      if (!discountTouched && proRata.discountAmount !== null && row.draft.discountAmount !== null) {
        patch.discountAmount = proRata.discountAmount;
      }
    }
    patchRow(row.key, patch, false);
    if (seeded.price || seeded.discount) {
      setRows((current) =>
        current.map((candidate) =>
          candidate.key === row.key
            ? { ...candidate, priceTouched, discountTouched }
            : candidate,
        ),
      );
    }
  }

  function addRow() {
    setRows((current) => [
      ...current,
      {
        key: freshKey(),
        saved: null,
        draft: blankDraft(),
        state: 'idle',
        error: null,
        fieldErrors: {},
        priceTouched: false,
        discountTouched: false,
      },
    ]);
  }

  function discardRow(key: string) {
    setRows((current) => current.filter((row) => row.key !== key));
  }

  /**
   * Save every dirty, unblocked row.
   *
   * ⚠️ **N requests, one at a time, with no transaction across them.** There is no
   * bulk endpoint, so a partial failure is an ordinary outcome: every row carries
   * its own status and reason, and the run reports "2 of 3 lines saved".
   *
   * ⚠️ **A retry never re-posts a row that already landed** — a successful POST
   * turns its row into a saved row with a real line id, so the next run either
   * skips it or PATCHes it.
   *
   * ⚠️ **`onChanged` fires ONCE, after the whole run**, and only when something
   * landed. "There was nothing to save" must never reach the parent as "the data
   * moved".
   */
  async function saveAll() {
    const targets = rows.filter((row) => dirtyKeys.has(row.key) && !blockedKeys.has(row.key));
    if (targets.length === 0) return;
    setSaving(true);
    setSummary(null);
    setRows((current) =>
      current.map((row) =>
        targets.some((target) => target.key === row.key)
          ? { ...row, state: 'saving', error: null, fieldErrors: {} }
          : row,
      ),
    );
    let saved = 0;
    let failed = 0;
    let sentCities = false;
    for (const target of targets) {
      try {
        let result: { data: CreditNoteLine };
        if (target.saved === null) {
          const body = createPayload(target.draft, undefined);
          result = await api.createCreditNoteLine(creditNoteId, body);
          if ('cityIds' in body) sentCities = true;
        } else {
          const body = updatePayload(target.draft, target.saved);
          result = await api.updateCreditNoteLine(creditNoteId, target.saved.id, body);
          if ('cityIds' in body) sentCities = true;
        }
        saved += 1;
        setRows((current) =>
          current.map((row) =>
            row.key === target.key ? { ...rowOf(result.data), key: row.key, state: 'saved' } : row,
          ),
        );
      } catch (caught: unknown) {
        failed += 1;
        const described = describeFailure(caught);
        setRows((current) =>
          current.map((row) =>
            row.key === target.key
              ? {
                  ...row,
                  state: 'failed',
                  // A city refusal is shown on the Cities control, not twice.
                  error: creditCityRefusalMessage(caught) !== null ? null : described.message,
                  fieldErrors: described.fieldErrors,
                }
              : row,
          ),
        );
        // A 401/403 will refuse every remaining row identically. Stop rather than
        // spend N requests reproducing one sentence.
        if (caught instanceof ApiError && (caught.status === 401 || caught.status === 403)) {
          setRows((current) =>
            current.map((row) => (row.state === 'saving' ? { ...row, state: 'idle' } : row)),
          );
          break;
        }
      }
    }
    setSaving(false);
    setSummary({ saved, failed });
    // A save that wrote cities moved `creditedCityIds`: re-read the invoice lines.
    if (sentCities) setInvoiceLinesReload((current) => current + 1);
    if (saved > 0) onChanged();
  }

  async function confirmDelete() {
    const target = pendingDelete;
    if (target === null || target.saved === null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteCreditNoteLine(creditNoteId, target.saved.id);
      setRows((current) => current.filter((row) => row.key !== target.key));
      setPendingDelete(null);
      // Deleting a line that credited cities frees them: re-read `creditedCityIds`.
      if (citiesOfLine(target.saved).length > 0) setInvoiceLinesReload((current) => current + 1);
      /*
       * ⚠️ **A delete moves the note's header totals exactly as a write does**,
       * and a `204` has no body — so the new figures arrive nowhere and the
       * parent has to re-read. Inside the `try` and after the `await`, so it
       * reports a delete the server accepted rather than one that was attempted.
       */
      onChanged();
    } catch (caught: unknown) {
      setDeleteError(describeFailure(caught).message);
    } finally {
      setDeleting(false);
    }
  }

  const readOnlyReason = editable
    ? null
    : 'This credit note’s lines can’t be changed — it has been approved or voided, or you don’t have permission.';
  const rowsDisabled = !editable || saving;

  /* ── One row's controls, rendered twice: the grid, and the card ────────── */

  function rowControls(row: Row, compact: boolean) {
    const problems = problemsFor(row);
    const common: ControlProps = {
      row,
      problems,
      disabled: rowsDisabled || row.state === 'saving',
      compact,
      onChange: (patch) => patchRow(row.key, patch),
    };
    return {
      problems,
      product: (
        <ProductControl
          {...common}
          options={productOptions}
          {...(products.failed
            ? { footnote: 'The product list could not be read, so it may be short.' }
            : {})}
        />
      ),
      description: <DescriptionControl {...common} />,
      cities: (
        <CitiesControl
          row={row}
          problems={problems}
          disabled={common.disabled}
          compact={compact}
          pickable={editable}
          lookup={lookupFor(row)}
          onCitiesChange={(values, invoiceLine) => patchCities(row, values, invoiceLine)}
        />
      ),
      quantity: <QuantityControl {...common} />,
      price: <PriceControl {...common} currency={currency} catalogPrice={catalogPriceOf(row)} />,
      discount: <DiscountControl {...common} currency={currency} />,
      tax: (
        <TaxRateControl
          {...common}
          rates={taxRates.rates}
          ratesLoading={taxRates.loading}
          ratesError={taxRates.error?.message ?? null}
          connected={xeroConnected}
        />
      ),
      amount: <AmountCell row={row} preview={previewFor(row)} currency={currency} />,
    };
  }

  function rowActions(row: Row) {
    if (row.saved === null) {
      return (
        <DisabledReason reason={readOnlyReason}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={rowsDisabled}
            onClick={() => discardRow(row.key)}
          >
            Discard
          </Button>
        </DisabledReason>
      );
    }
    // Icon-only, so `Button` shows `title` as its styled tooltip — the action
    // name when live, the why-disabled sentence when read-only — and wraps a
    // disabled control itself. No `DisabledReason` span: its `title` would be a
    // second label beside the tooltip.
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        leadingIcon={<TrashIcon />}
        aria-label="Delete this line"
        title={readOnlyReason ?? 'Delete this line'}
        disabled={rowsDisabled}
        onClick={() => {
          setDeleteError(null);
          setPendingDelete(row);
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="grid min-w-0 gap-2" aria-busy="true">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  /*
   * ⚠️ **THE THREE STATES STAY APART.** A failed read gets the error and NO
   * table: an empty grid over a read that never came back is indistinguishable
   * from a credit note that genuinely has no lines, and one of those invites
   * somebody to type them in again.
   */
  if (loadError !== null) {
    return <ErrorBlock title="The line items couldn’t be loaded.">{loadError}</ErrorBlock>;
  }

  return (
    <div className="grid min-w-0 gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <DisabledReason reason={readOnlyReason}>
            <Button type="button" variant="secondary" disabled={rowsDisabled} onClick={addRow}>
              Add row
            </Button>
          </DisabledReason>
          <DisabledReason reason={readOnlyReason}>
            <Button
              type="button"
              variant="secondary"
              disabled={rowsDisabled}
              onClick={() => setCopyOpen(true)}
            >
              Copy from an invoice
            </Button>
          </DisabledReason>
        </div>
        {dirtyCount > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {/* The count is NAMED, never a bare disabled Save — a control that
                refuses to act without saying what is stopping it is a claim
                nobody can check. */}
            {blockedKeys.size > 0 && (
              <span className="text-xs text-warning">
                {blockedKeys.size} row{blockedKeys.size === 1 ? '' : 's'} need attention first.
              </span>
            )}
            <DisabledReason reason={readOnlyReason}>
              <Button
                type="button"
                variant="primary"
                loading={saving}
                disabled={rowsDisabled || blockedKeys.size > 0}
                onClick={() => void saveAll()}
              >
                Save {dirtyCount} row{dirtyCount === 1 ? '' : 's'}
              </Button>
            </DisabledReason>
          </div>
        )}
      </div>

      {/* Said ONCE, above the grid, rather than under every disabled control: a
          reader meets four or five of them per row, and one sentence per cell is
          a screen of the same refusal. */}
      {readOnlyReason !== null && <p className="text-xs text-fg-muted">{readOnlyReason}</p>}

      {summary !== null && (
        <p className={cx('text-xs', summary.failed > 0 ? 'text-warning' : 'text-fg-muted')}>
          {summary.failed > 0
            ? `${summary.saved} of ${summary.saved + summary.failed} lines saved. The rest are marked below.`
            : `${summary.saved} line${summary.saved === 1 ? '' : 's'} saved.`}
        </p>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title="No lines yet"
          description={
            editable
              ? 'Copy the lines from the invoice being credited, or add one by hand.'
              : 'This credit note has no line items.'
          }
          action={
            editable ? (
              <Button type="button" variant="primary" onClick={() => setCopyOpen(true)}>
                Copy from an invoice
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          {/*
            The wide grid, and the stacked cards below `md`. Two renderings of one
            row set, never two sources of truth: both call `rowControls`, and only
            `compact` differs.
          */}
          <div className="hidden min-w-0 md:block">
            <TableWrap frozenHeader frozenMaxHeight="min(60vh, 560px)">
              <Table className="min-w-[1587px] table-fixed">
                {/*
                  ⚠️ **The `<colgroup>` is the ONE place a column width lives**,
                  and under `table-fixed` these values are authoritative — no
                  `<th>` or `<td>` below carries a `w-`, so nothing can disagree
                  with them. Each body cell instead carries a content `min-w-` of
                  its column MINUS 24px, which is `TD`'s `px-3` twice; a content
                  minimum is what the algorithm may not go under.
                  **Those two lists are the one thing here that can silently drift
                  apart — move them together, always.**

                  Item     190 · Cities  190 · Description 377 · Qty 84 ·
                  Price    148 · Discount 240 · Tax rate 142 · Amount 122 ·
                  actions   94                                      = 1587

                  ⚠️ Cities (owner 2026-10-27) is the invoice grid's 190 for a
                  count-only MultiCombobox trigger; read-only chips wrap in it.

                  ⚠️ **Item is 190 where the invoice grid's is 285, and that is
                  the ONE width that differs.** There it holds a STACK — the
                  product, its project, the "all cities" flag, a city picker and a
                  regions picker — and 190 is the measured width it had before
                  those moved in. Here the cell holds the product picker and
                  nothing else, because a credit-note line has no project, city,
                  region or inclusion controls at all. Every other column does the
                  same job in the same space and keeps its width.

                  ⚠️ **Discount is the one column NOT cut from its figure.** 216px
                  of content is the SEGMENTED CONTROL, measured at 193 in Chrome
                  (`Percentage` 101 + `Amount` 84 + a 2px gap + `p-0.5` + a 2px
                  border), leaving 23px for a font that renders wider. A reader
                  re-cutting it from the widest amount it must display will size it
                  too narrow and clip a mode label.

                  Widths are cut at `--text-sm` (13px, tabular-nums) from what each
                  cell must show in its WORST case, never from today's data:
                  `formatDecimalString` renders thousands separators, so Amount and
                  Price each hold `123,456.00` (≈71px) with room to spare — and
                  Amount must also hold a LEADING MINUS, because a deduction line
                  is ordinary here.

                  The last value is the actions column's width and must equal the
                  `right-[94px]` offset in PIN_AMOUNT.
                */}
                <colgroup>
                  <col className="w-[190px]" />
                  <col className="w-[190px]" />
                  <col className="w-[377px]" />
                  <col className="w-[84px]" />
                  <col className="w-[148px]" />
                  {/* Discount — sized by the SEGMENTED CONTROL, not the figure. */}
                  <col className="w-[240px]" />
                  <col className="w-[142px]" />
                  <col className="w-[122px]" />
                  <col className="w-[94px]" />
                </colgroup>
                <THead>
                  <TR>
                    <TH>Item</TH>
                    <TH>Cities</TH>
                    <TH>Description</TH>
                    <TH numeric>Qty</TH>
                    <TH numeric>Price</TH>
                    {/* Not "Disc %": the box takes either, and the segmented
                        control in the cell says which it is currently taking. */}
                    <TH numeric>Discount</TH>
                    <TH>Tax rate</TH>
                    {/* Pinned header pair under the frozen header row: the
                        primitive's sticky literals, never PIN_AMOUNT's, since
                        two z-index or shadow utilities on one cell race. 94 is
                        the actions column, as in PIN_AMOUNT. */}
                    <TH numeric frozen="right" frozenOffset={94}>
                      Amount
                    </TH>
                    <TH className="right-0">
                      <span className="sr-only">Status and actions</span>
                    </TH>
                  </TR>
                </THead>
                <TBody>
                  {rows.map((row) => {
                    const controls = rowControls(row, true);
                    const dirty = dirtyKeys.has(row.key);
                    return (
                      <Fragment key={row.key}>
                        <TR className={cx(row.state === 'failed' && 'bg-danger-soft/40')}>
                          <TD className="align-top">
                            <div className="min-w-[166px]">{controls.product}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[166px]">{controls.cities}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[353px]">{controls.description}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[60px]">{controls.quantity}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[124px]">{controls.price}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[216px]">{controls.discount}</div>
                          </TD>
                          <TD className="align-top">
                            <div className="min-w-[118px]">{controls.tax}</div>
                          </TD>
                          {/* The pinned pair. The background is chosen by a
                              ternary rather than by two `bg-` utilities on one
                              element: same-property Tailwind utilities resolve by
                              stylesheet order, not by the order written. */}
                          <TD
                            numeric
                            className={cx(
                              'align-top',
                              PIN_AMOUNT,
                              row.state === 'failed' ? PIN_ROW_BG_FAILED : PIN_ROW_BG,
                            )}
                          >
                            <div className="min-w-[98px]">{controls.amount}</div>
                          </TD>
                          <TD
                            className={cx(
                              'align-top',
                              PIN_ACTIONS,
                              row.state === 'failed' ? PIN_ROW_BG_FAILED : PIN_ROW_BG,
                            )}
                          >
                            <div className="grid min-w-[70px] justify-items-end gap-1">
                              <StatusBadge row={row} dirty={dirty} />
                              {rowActions(row)}
                            </div>
                          </TD>
                        </TR>
                        {controls.problems.error !== null && (
                          <TR>
                            <TD colSpan={COLUMN_COUNT} className="pt-0">
                              <ErrorBlock title={controls.problems.error} />
                            </TD>
                          </TR>
                        )}
                      </Fragment>
                    );
                  })}
                </TBody>
              </Table>
            </TableWrap>
          </div>

          {/*
            Below `md` a row is a CARD: the same controls, the same state and the
            same validation, with `compact` false — so every control keeps its
            label and its hint, and `table-fixed` never reaches it. A feature
            reachable only above a breakpoint is indistinguishable from one that
            was removed.
          */}
          <ul className="grid gap-3 md:hidden">
            {rows.map((row) => {
              const controls = rowControls(row, false);
              const dirty = dirtyKeys.has(row.key);
              return (
                <li
                  key={row.key}
                  className={cx(
                    'rounded-md border border-border bg-surface',
                    row.state === 'failed' && 'border-danger-border',
                  )}
                >
                  <div className="grid gap-3 px-3 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <StatusBadge row={row} dirty={dirty} />
                      <div className="text-right">
                        <span className="block text-2xs text-fg-muted">
                          Amount
                        </span>
                        {controls.amount}
                      </div>
                    </div>
                    {controls.product}
                    {controls.cities}
                    {controls.description}
                    <div className="grid gap-3 sm:grid-cols-2">
                      {controls.quantity}
                      {controls.discount}
                    </div>
                    {controls.price}
                    {controls.tax}
                    {controls.problems.error !== null && (
                      <ErrorBlock title={controls.problems.error} />
                    )}
                    <div className="flex justify-end">{rowActions(row)}</div>
                  </div>
                </li>
              );
            })}
          </ul>

          {/*
            The label says WHICH figure this is. The credit note's own four header
            totals are the SERVER's and are rewritten by it on every line write;
            this figure has never been them.
          */}
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-t border-border pt-2">
            <span className="text-xs text-fg-muted">
              {dirtyCount > 0 ? 'Total of saved lines' : 'Total'}
            </span>
            <Money amount={savedTotal} currency={currency} className="text-sm font-semibold text-fg" />
          </div>
        </>
      )}

      <CopyFromInvoiceDialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        creditNoteId={creditNoteId}
        currency={currency}
        onCopied={(lines) => {
          /*
           * ⚠️ **Appended, not replacing.** A copy adds to whatever is already on
           * the note — somebody may be crediting two invoices — and rows already
           * on screen that have never been saved must survive it.
           */
          setRows((current) => [...current, ...lines.map(rowOf)]);
          // The copy credited cities: re-read `creditedCityIds`.
          setInvoiceLinesReload((current) => current + 1);
          onChanged();
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={
          pendingDelete?.saved
            ? `Delete the ${pendingDelete.saved.product.name} line?`
            : 'Delete this line?'
        }
        tone="danger"
        loading={deleting}
        {...(deleteError ? { error: deleteError } : {})}
        confirmLabel="Delete line"
        description={
          pendingDelete?.saved ? (
            <>
              <p>
                <strong>{pendingDelete.saved.product.name}</strong> will be removed from this credit
                note, and its {formatDecimalString(pendingDelete.saved.lineTotal)} {currency} will
                come off the note’s total.
              </p>
              {/* The wording is frozen and stored on the line, so it is destroyed
                  with it — there is no derivation to rebuild it from. */}
              <p className="mt-2 text-fg-muted">{pendingDelete.saved.description}</p>
            </>
          ) : (
            <p>This line will be removed from the credit note.</p>
          )
        }
      />
    </div>
  );
}
